import { useRouterHelpers } from '@/features/common/composables';
import { TripSearchRequest, WalkSpeedType } from '@/features/trips/models';
import { useTripSearchStore } from '@/features/trips/stores';
import { GetTripModeDto, PlaceAutocompleteModelDto, PlaceType } from '@/types/webapi';
import { isEqual } from 'lodash-es';
import { storeToRefs } from 'pinia';
import { nextTick, onBeforeUnmount, watch, type WatchStopHandle } from 'vue';
import { useRouter, type LocationQuery, type LocationQueryRaw } from 'vue-router';
import { DateType } from '@/features/common/models';
import { isKindaEqual } from '@/features/trips/helpers';
import { useDisplay } from 'vuetify';
import { usePlacesStore } from '@/features/places/stores';

export function useQueryParams() {
    const router = useRouter();
    const store = useTripSearchStore();
    const { mobile } = useDisplay();
    const placesStore = usePlacesStore();

    const { searchRequest, selectedTripPatternId, selectedTripPatternIdIsExpanded } = storeToRefs(store);
    const { fromPlace, toPlace } = storeToRefs(placesStore);
    const { onHistoryPopState } = useRouterHelpers();
    let skipRouterPush = false;

    function parseQueryParams(query: LocationQuery): {
        searchRequest: TripSearchRequest;
        fromPlace: PlaceAutocompleteModelDto | null;
        toPlace: PlaceAutocompleteModelDto | null;
        selectedTripPatternId: string | null;
        selectedTripPatternIdIsExpanded: boolean;
    } {
        const {
            fromId,
            toId,
            dateTime: _dateTime,
            arriveBy,
            modes: _modes,
            lines: _lines,
            walkSpeed: _walkSpeed,
            transferSlack: _transferSlack,
            showTripsWithTransfers: _showTripsWithTransfers,
            selectedTripPatternId: _selectedTripPatternId,
            selectedTripPatternIdIsExpanded: _selectedTripPatternIdIsExpanded
        } = query;

        const parsedFromPlace = fromId
            ? fromId === fromPlace.value?.id
                ? fromPlace.value
                : ({ id: fromId as string, title: fromId as string, type: PlaceType.Unknown } satisfies PlaceAutocompleteModelDto)
            : null;
        const parsedToPlace = toId
            ? toId === toPlace.value?.id
                ? toPlace.value
                : ({ id: toId as string, title: toId as string, type: PlaceType.Unknown } satisfies PlaceAutocompleteModelDto)
            : null;
        const dateTime = _dateTime ? new Date(_dateTime as string) : undefined;
        const dateTimeType = arriveBy ? DateType.ArriveBy : dateTime ? DateType.DepartAt : DateType.Now;
        const modes = (_modes ? (_modes as string).split(',') : []) as GetTripModeDto[];
        const lines = (_lines ? (_lines as string).split(',') : []) as string[];
        const walkSpeed = _walkSpeed ? parseInt(_walkSpeed as string) : WalkSpeedType.Normal;
        const transferSlack = _transferSlack ? parseInt(_transferSlack as string) : 120;
        const showTripsWithTransfers = !(_showTripsWithTransfers === 'false');
        const selectedTripPatternId = (_selectedTripPatternId as string) || null;
        const selectedTripPatternIdIsExpanded = _selectedTripPatternIdIsExpanded === 'true';

        return {
            searchRequest: {
                dateTime,
                dateTimeType,
                filters: { modes, lines, walkSpeed, transferSlack, showTripsWithTransfers }
            },
            fromPlace: parsedFromPlace,
            toPlace: parsedToPlace,
            selectedTripPatternId,
            selectedTripPatternIdIsExpanded
        };
    }

    function buildQueryParams(state: {
        searchRequest: TripSearchRequest;
        fromPlace: PlaceAutocompleteModelDto | null;
        toPlace: PlaceAutocompleteModelDto | null;
        selectedTripPatternId?: string | null;
        selectedTripPatternIdIsExpanded?: boolean;
    }) {
        const { searchRequest, fromPlace, toPlace, selectedTripPatternId, selectedTripPatternIdIsExpanded } = state;
        const { dateTime, dateTimeType, filters } = searchRequest;
        const { modes, walkSpeed, transferSlack, showTripsWithTransfers, lines } = filters;

        return {
            fromId: fromPlace?.id,
            toId: toPlace?.id,
            dateTime: dateTime ? dateTime.toISOString() : undefined,
            arriveBy: dateTimeType === DateType.ArriveBy ? 'true' : undefined,
            modes: modes.length ? modes.join(',') : undefined,
            lines: lines.length ? lines.join(',') : undefined,
            walkSpeed: walkSpeed !== WalkSpeedType.Normal ? walkSpeed.toString() : undefined,
            transferSlack: transferSlack !== 120 ? transferSlack.toString() : undefined,
            showTripsWithTransfers: !showTripsWithTransfers ? 'false' : undefined,
            selectedTripPatternId: selectedTripPatternId ? selectedTripPatternId : undefined,
            selectedTripPatternIdIsExpanded: selectedTripPatternIdIsExpanded ? 'true' : undefined
        };
    }

    function updateQueryParams(query: LocationQueryRaw) {
        if (skipRouterPush) {
            skipRouterPush = false;
            return;
        }

        if (isEqual(query, router.currentRoute.value.query)) return;

        // preserve existing query params, and override relevant ones
        // we have to specify the route name and params to clear the params when initiating a new search from the trip details page
        nextTick(() =>
            router.push({
                name: router.currentRoute.value.name,
                params: {},
                query: { ...router.currentRoute.value.query, ...query }
            })
        );
    }

    let watcher: WatchStopHandle | undefined;
    function init() {
        onHistoryPopState(currentRoute => {
            skipRouterPush = true;

            const {
                searchRequest: _searchRequest,
                selectedTripPatternId: _selectedTripPatternId,
                selectedTripPatternIdIsExpanded: _selectedTripPatternIdIsExpanded
            } = parseQueryParams(currentRoute.query);

            // if search request has changed, reset the store value to trigger search
            if (
                !isKindaEqual(
                    { ...searchRequest.value, fromPlace: fromPlace.value, toPlace: toPlace.value },
                    { ..._searchRequest, fromPlace: fromPlace.value, toPlace: toPlace.value }
                )
            ) {
                searchRequest.value = _searchRequest;
            }

            selectedTripPatternId.value = _selectedTripPatternId;
            selectedTripPatternIdIsExpanded.value = _selectedTripPatternIdIsExpanded;
        });

        // start watcher to update query params when searchRequest value changes
        watcher = watch(
            [() => JSON.stringify(searchRequest.value), fromPlace, toPlace, selectedTripPatternId, selectedTripPatternIdIsExpanded],
            (value, oldValue) => {
                if (isEqual(value, oldValue)) return;

                const [_a, _b, _c, _d, expanded] = value;
                const [_oldA, _oldB, _oldC, _oldD, oldExpanded] = oldValue;

                // this prevents the query params from being updated when the user is on mobile and the trip pattern is expanded/collapsed
                if (mobile.value && expanded !== oldExpanded) {
                    skipRouterPush = true;
                }

                updateQueryParams(
                    buildQueryParams({
                        searchRequest: searchRequest.value,
                        fromPlace: fromPlace.value,
                        toPlace: toPlace.value,
                        selectedTripPatternId: selectedTripPatternId.value,
                        selectedTripPatternIdIsExpanded: selectedTripPatternIdIsExpanded.value
                    })
                );
            }
        );
    }

    onBeforeUnmount(() => watcher?.());

    function patchStore() {
        const { fromPlace, toPlace, ...tripStoreParams } = parseQueryParams(router.currentRoute.value.query);

        store.$patch(tripStoreParams);
        placesStore.$patch({ fromPlace, toPlace });
    }

    return {
        init,
        patchStore,
        buildQueryParams
    };
}
